import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, CreditCard, CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";
import Button from "@/components/Button";
import StripeCardModal from "@/components/StripeCardModal";
import { Spinner } from "@/components/States";
import { api } from "@/lib/api";

export default function PaymentMethods() {
  const navigate = useNavigate();
  const [method, setMethod] = useState(null);
  const [cardSecret, setCardSecret] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => api("/payments/method").then(setMethod).catch(() => setMethod({ enabled: false }));
  useEffect(() => { load(); }, []);

  const addCard = async () => {
    setErr(null);
    try {
      const r = await api("/payments/setup-intent", { method: "POST" });
      setCardSecret(r.client_secret);
    } catch (e) {
      setErr(e?.message || "Could not start card setup. Please try again.");
      toast.error(e?.message || "Could not start card setup.");
    }
  };

  const onSaved = async (setupIntentId) => {
    try {
      await api("/payments/setup-complete", { method: "POST", body: { setup_intent_id: setupIntentId } });
      setCardSecret(null);
      toast.success("Card saved");
      load();
    } catch (e) {
      toast.error(e?.message || "Could not save card.");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center gap-3 border-b border-border bg-card px-4 py-3">
        <button onClick={() => navigate(-1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-foreground">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-bold text-foreground">Payment methods</h1>
      </header>

      <div className="mx-auto max-w-md px-5 py-8">
        {err && (
          <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive" data-testid="payment-error">
            {err}
          </div>
        )}
        {method === null ? (
          <div className="flex justify-center py-16"><Spinner className="h-7 w-7 text-brand-600" /></div>
        ) : !method.enabled ? (
          <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            Card payments aren&apos;t configured yet. You&apos;ll pay after your trip completes.
          </div>
        ) : method.has_card ? (
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-brand-600 to-brand-700 text-white">
                <CreditCard className="h-6 w-6" />
              </span>
              <div className="flex-1">
                <p className="font-bold capitalize text-foreground">{method.brand} •••• {method.last4}</p>
                <p className="flex items-center gap-1 text-xs text-success"><CheckCircle2 className="h-3.5 w-3.5" /> Default payment method</p>
              </div>
            </div>
            <Button variant="secondary" className="mt-4 w-full" onClick={addCard} data-testid="replace-card"><Plus className="h-4 w-4" /> Replace card</Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
            <span className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-brand-600"><CreditCard className="h-7 w-7" /></span>
            <p className="font-bold text-foreground">No card on file</p>
            <p className="mt-1 text-sm text-muted-foreground">Add a card for faster checkout and to accept driver offers.</p>
            <Button className="mt-5 w-full" onClick={addCard} data-testid="add-card"><Plus className="h-4 w-4" /> Add card</Button>
          </div>
        )}
      </div>

      <StripeCardModal open={!!cardSecret} clientSecret={cardSecret} onSaved={onSaved} onClose={() => setCardSecret(null)} />
    </div>
  );
}
